import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const DEV_ACCOUNTS = [
  { email: "almira@indielaw.com", name: "Almira Avila", role: "Teammate" },
  { email: "reyna@indielaw.com", name: "Reyna Fisher", role: "Reviewer" },
  { email: "stephen@indielaw.com", name: "Stephen Reich", role: "Attorney" },
  { email: "joey@indielaw.com", name: "Joey Vitale", role: "Admin" },
  { email: "katie@indielaw.com", name: "Katie Soy", role: "Marketing" },
];

const IS_PREVIEW = /preview\.emergentagent\.com|localhost/.test(
  typeof window !== "undefined" ? window.location.hostname : ""
);

export default function Login() {
  const { user, checking, loginGoogle, devLogin } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!checking && user) navigate("/", { replace: true });
  }, [checking, user, navigate]);

  const dev = async (email) => {
    setLoading(true);
    try {
      await devLogin(email);
      navigate("/");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't sign you in.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left brand panel */}
      <div className="relative hidden lg:flex flex-col justify-between p-12 bg-[#141B4D] text-white overflow-hidden">
        <div className="absolute inset-0 opacity-25"
          style={{ backgroundImage: "url(https://images.pexels.com/photos/30185858/pexels-photo-30185858.jpeg)", backgroundSize: "cover", backgroundPosition: "center" }} />
        <div className="absolute inset-0 bg-[#141B4D]/70" />
        <div className="relative">
          <div className="flex items-center gap-2.5">
            <div className="w-10 h-10 rounded-lg bg-[#FFD100] flex items-center justify-center">
              <ShieldCheck className="w-6 h-6 text-[#141B4D]" />
            </div>
            <span className="font-varsity text-2xl text-[#FFD100]">Indie University</span>
          </div>
        </div>
        <div className="relative max-w-md">
          <h1 className="text-4xl xl:text-5xl font-bold leading-tight">Welcome to campus.</h1>
          <p className="mt-5 text-white/70 text-lg">This is the Indie Law team's home base. Training, playbooks, and the client tools you use every day — all in one place. Everything you need to do your best work.</p>
          <p className="mt-8 text-[#FFD100] font-semibold">Learn the ropes. Master the process.</p>
        </div>
        <div className="relative text-white/40 text-sm">Indie University · Internal team hub</div>
      </div>

      {/* Right sign-in */}
      <div className="flex items-center justify-center p-8 bg-[#F2E9DB]">
        <div className="w-full max-w-sm brand-fade">
          <h2 className="text-3xl font-bold text-[#141B4D]">Welcome back</h2>
          <p className="text-[#101820]/60 mt-1.5">Sign in with your Indie Law Google account.</p>

          <Button data-testid="google-signin" onClick={loginGoogle}
            className="mt-8 w-full h-12 bg-white text-[#101820] font-semibold border border-[#BDC5DB] hover:bg-white hover:brightness-95 shadow-sm flex items-center justify-center gap-3">
            <svg width="20" height="20" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"/><path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"/><path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"/><path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571.001-.001.002-.001.003-.002l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"/></svg>
            Sign in with Google
          </Button>
          <p className="text-[11px] text-[#101820]/50 mt-3">Access is limited to <span className="font-semibold">@indielaw.com</span> accounts.</p>

          {IS_PREVIEW && (
            <div className="mt-8 pt-6 border-t border-[#BDC5DB]">
              <div className="text-xs font-bold uppercase tracking-widest text-[#101820]/50 mb-2">Preview quick sign-in (dev only)</div>
              <div className="grid grid-cols-2 gap-2">
                {DEV_ACCOUNTS.map((q) => (
                  <button key={q.email} data-testid={`dev-login-${q.role.toLowerCase()}`} disabled={loading}
                    onClick={() => dev(q.email)}
                    className="text-left bg-white border border-[#BDC5DB] rounded-lg px-3 py-2 hover:border-[#1D57A5] transition-all card-lift disabled:opacity-50">
                    <div className="text-sm font-semibold text-[#141B4D]">{q.name}</div>
                    <div className="text-[11px] text-[#1D57A5]">{q.role}</div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
